import React, { useState, useEffect } from "react";
import { Card, Table, Button, Input, Select, Tabs, Badge, Empty, Modal, Form, Divider, Radio, Dropdown, Space } from "antd";
import { SearchOutlined, FilterOutlined, EyeOutlined, EditOutlined, ToolOutlined, CloseOutlined, CheckCircleOutlined, InfoCircleOutlined, CalendarOutlined, TagOutlined, MoreOutlined } from "@ant-design/icons";
import Topbar from "../../components/TopbarNav";
import HOSidebar from "../../components/MasterSideBar";
import axios from "axios";
import { toast } from "react-toastify";

const { Option } = Select;
const { TabPane } = Tabs;
const { TextArea } = Input;
const API_URL = "https://ims.holdthechild.net";

// Mobile Responsive Asset Detail Modal Component
const EnhancedAssetDetailModal = ({ open, onClose, asset, maintenanceLogs, onUpdateStatus, onDisposal }) => {
  if (!asset) return null;

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width="100%"
      style={{ maxWidth: "500px", top: 10, margin: 0 }}
      closeIcon={<CloseOutlined className="text-gray-500 hover:text-gray-700" />}
      className="mobile-modal"
      centered={false}
      destroyOnClose={true}
    >
      <div className="p-3 sm:p-4">
        {/* Header */}
        <div className="flex items-start gap-3 mb-4">
          <div className="bg-orange-50 p-2 rounded-lg flex-shrink-0">
            <ToolOutlined className="text-orange-400 text-lg sm:text-xl" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-lg sm:text-xl font-semibold text-gray-800 mb-1 truncate">{asset.name}</h2>
            <p className="text-gray-600 text-xs sm:text-sm">Serial: {asset.serialNumber}</p>
          </div>
          <Badge 
            color={asset.status === "Active" ? "green" : "orange"} 
            text={<span className="text-xs">{asset.status}</span>}
          />
        </div>

        <Divider className="my-3" />

        {/* Asset Details Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3 mb-4">
          <div className="bg-gray-50 p-3 rounded-lg border">
            <div className="flex items-center gap-2 mb-1">
              <TagOutlined className="text-orange-400 text-sm" />
              <span className="text-xs font-medium text-gray-600 uppercase">Asset Code</span>
            </div>
            <p className="text-sm sm:text-base font-semibold text-gray-800 truncate">{asset.code || "N/A"}</p>
          </div>

          <div className="bg-gray-50 p-3 rounded-lg border">
            <div className="flex items-center gap-2 mb-1">
              <InfoCircleOutlined className="text-orange-400 text-sm" />
              <span className="text-xs font-medium text-gray-600 uppercase">Category</span>
            </div>
            <p className="text-sm sm:text-base font-semibold text-gray-800 truncate">{asset.category || "N/A"}</p>
          </div>

          <div className="bg-gray-50 p-3 rounded-lg border">
            <div className="flex items-center gap-2 mb-1">
              <CalendarOutlined className="text-orange-400 text-sm" />
              <span className="text-xs font-medium text-gray-600 uppercase">Last Service</span>
            </div>
            <p className="text-sm sm:text-base font-semibold text-gray-800">
              {asset.lastServiced ? new Date(asset.lastServiced).toLocaleDateString("en-GB") : "N/A"}
            </p>
          </div>

          <div className="bg-gray-50 p-3 rounded-lg border">
            <div className="flex items-center gap-2 mb-1">
              <CalendarOutlined className="text-orange-400 text-sm" />
              <span className="text-xs font-medium text-gray-600 uppercase">Next Service</span>
            </div>
            <p className="text-sm sm:text-base font-semibold text-gray-800">
              {asset.nextMaintenanceDate ? new Date(asset.nextMaintenanceDate).toLocaleDateString("en-GB") : "N/A"}
            </p>
          </div>
        </div>

        {/* Maintenance Logs */}
        {maintenanceLogs && maintenanceLogs.length > 0 && (
          <>
            <Divider className="my-3" />
            <h3 className="text-base font-semibold text-gray-800 mb-2">Maintenance History</h3>
            <div className="max-h-32 sm:max-h-40 overflow-y-auto space-y-2">
              {maintenanceLogs.map((log, index) => (
                <div key={index} className="bg-gray-50 p-2 rounded-lg border-l-2 border-orange-400">
                  <div className="flex justify-between items-start gap-2">
                    <span className="font-medium text-gray-800 text-xs sm:text-sm flex-1">{log.description || "Maintenance"}</span>
                    <span className="text-xs text-gray-500 whitespace-nowrap">
                      {log.date ? new Date(log.date).toLocaleDateString("en-GB") : "N/A"}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Action Buttons */}
        <Divider className="my-3" />
        <div className="flex flex-col sm:flex-row gap-2">
          <Button
            type="primary"
            onClick={onUpdateStatus}
            className="flex-1 h-9 bg-orange-400 hover:bg-orange-500 border-orange-400 hover:border-orange-500 text-sm"
            size="middle"
          >
            Update Status
          </Button>
          <Button
            onClick={onDisposal}
            className="flex-1 h-9 border-orange-400 text-orange-400 hover:border-orange-500 hover:text-orange-500 text-sm"
            size="middle"
          >
            Request Disposal/Replacement
          </Button>
        </div>
      </div>
    </Modal>
  );
};

// Mobile Responsive Status Modal Component
const EnhancedStatusModal = ({ open, onClose, currentStatus, statusOptions, onStatusChange, onSave }) => {
  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width="100%"
      style={{ maxWidth: "450px", top: 10, margin: 0 }}
      closeIcon={<CloseOutlined className="text-gray-500 hover:text-gray-700" />}
      className="mobile-modal"
      centered={false}
      destroyOnClose={true}
    >
      <div className="p-3 sm:p-4">
        <div className="flex items-center gap-3 mb-4">
          <div className="bg-blue-50 p-2 rounded-lg flex-shrink-0">
            <CheckCircleOutlined className="text-blue-400 text-lg" />
          </div>
          <div className="flex-1">
            <h2 className="text-lg font-semibold text-gray-800 m-0">Update Asset Status</h2>
            <p className="text-gray-600 text-xs">Select a new status for this asset</p>
          </div>
        </div>

        <Form layout="vertical">
          <Form.Item label={<span className="font-medium text-gray-700">Status</span>}>
            <Radio.Group 
              value={currentStatus} 
              onChange={(e) => onStatusChange(e.target.value)}
              className="w-full"
            >
              <div className="grid grid-cols-1 gap-2">
                {statusOptions.map((option) => (
                  <Radio 
                    key={option.value} 
                    value={option.value}
                    className="w-full p-2 sm:p-3 rounded border hover:border-orange-300 hover:bg-orange-50 transition-colors text-sm sm:text-base"
                  >
                    {option.label}
                  </Radio>
                ))}
              </div>
            </Radio.Group>
          </Form.Item>
        </Form>

        <Divider className="my-3" />

        <div className="flex flex-col sm:flex-row gap-2">
          <Button onClick={onClose} className="flex-1 h-9 text-sm" size="middle">
            Cancel
          </Button>
          <Button
            type="primary"
            onClick={onSave}
            className="flex-1 h-9 bg-orange-400 hover:bg-orange-500 border-orange-400 hover:border-orange-500 text-sm"
            size="middle"
          >
            Save Changes
          </Button>
        </div>
      </div>
    </Modal>
  );
};

// Mobile Responsive Disposal Modal Component
const EnhancedDisposalModal = ({ open, onClose, disposalData, setDisposalData, onSubmit }) => {
  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width="100%"
      style={{ maxWidth: "500px", top: 10, margin: 0 }}
      closeIcon={<CloseOutlined className="text-gray-500 hover:text-gray-700" />}
      className="mobile-modal"
      centered={false}
      destroyOnClose={true}
    >
      <div className="p-3 sm:p-4">
        <div className="flex items-center gap-3 mb-4">
          <div className="bg-red-50 p-2 rounded-lg flex-shrink-0">
            <EditOutlined className="text-red-400 text-lg" />
          </div>
          <div className="flex-1">
            <h2 className="text-lg font-semibold text-gray-800 m-0">Disposal/Replacement Request</h2>
            <p className="text-gray-600 text-xs">Submit a request for asset disposal or replacement</p>
          </div>
        </div>

        <Form layout="vertical">
          <Form.Item 
            label={<span className="font-medium text-gray-700">Request Type</span>}
            required
          >
            <Select
              value={disposalData.requestType}
              onChange={(value) => setDisposalData({ ...disposalData, requestType: value })}
              placeholder="Select request type"
              size="middle"
              className="w-full"
            >
              <Option value="Disposal">Disposal</Option>
              <Option value="Replacement">Replacement</Option>
            </Select>
          </Form.Item>

          <Form.Item 
            label={<span className="font-medium text-gray-700">Reason</span>}
            required
          >
            <Input
              value={disposalData.reason}
              onChange={(e) => setDisposalData({ ...disposalData, reason: e.target.value })}
              placeholder="Brief reason for the request"
              size="middle"
            />
          </Form.Item>

          <Form.Item 
            label={<span className="font-medium text-gray-700">Justification</span>}
            required
          >
            <TextArea
              value={disposalData.justification}
              onChange={(e) => setDisposalData({ ...disposalData, justification: e.target.value })}
              placeholder="Provide detailed justification for this request"
              rows={3}
              size="middle"
            />
          </Form.Item>
        </Form>

        <Divider className="my-3" />

        <div className="flex flex-col sm:flex-row gap-2">
          <Button onClick={onClose} className="flex-1 h-9 text-sm" size="middle">
            Cancel
          </Button>
          <Button
            type="primary"
            onClick={onSubmit}
            disabled={!disposalData.requestType || !disposalData.reason || !disposalData.justification}
            className="flex-1 h-9 bg-orange-400 hover:bg-orange-500 border-orange-400 hover:border-orange-500 text-sm"
            size="middle"
          >
            Submit Request
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default function EquipmentDashboard() {
  const [openDetail, setOpenDetail] = useState(false);
  const [openStatus, setOpenStatus] = useState(false);
  const [openDisposal, setOpenDisposal] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [newStatus, setNewStatus] = useState(null);
  const [disposalData, setDisposalData] = useState({
    requestType: "",
    reason: "",
    justification: "",
  });

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [sidebarVisible, setSidebarVisible] = useState(false);
  const [activeTab, setActiveTab] = useState("1");

  const [assets, setAssets] = useState([]);
  const [disposalRequests, setDisposalRequests] = useState([]);
  const [replacementRequests, setReplacementRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [maintenanceLogs, setMaintenanceLogs] = useState([]);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [disposalPage, setDisposalPage] = useState(1);
  const [replacementPage, setReplacementPage] = useState(1);
  const [requestPageSize] = useState(5);

  const fetchAssets = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem("token");
      if (!token) throw new Error("Authentication token not found.");
      const response = await axios.get(`${API_URL}/api/assets`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const sortedAssets = (response.data.data || []).sort(
        (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
      );
      setAssets(sortedAssets);
      setLoading(false);
    } catch (err) {
      console.error("Failed to fetch assets:", err);
      setError("Failed to fetch assets. Please try again later.");
      setLoading(false);
      toast.error("Failed to load assets. 😢");
    }
  };

  const fetchDisposalRequests = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API_URL}/api/disposal-requests`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const sorted = (res.data.data || []).sort(
        (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
      );
      setDisposalRequests(sorted);
    } catch (err) {
      console.error("Failed to fetch disposal requests:", err);
      toast.error("Failed to load disposal requests ❌");
    }
  };

  const fetchReplacementRequests = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API_URL}/api/disposal-requests`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const sorted = (res.data.data || []).sort(
        (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
      );
      setReplacementRequests(sorted);
    } catch (err) {
      console.error("Failed to fetch replacement requests:", err);
      toast.error("Failed to load replacement requests ❌");
    }
  };

  useEffect(() => {
    fetchAssets();
    fetchDisposalRequests();
    fetchReplacementRequests();
  }, []);

  const addRequestLocally = (newRequest) => {
    if (newRequest.requestType === "Disposal") {
      setDisposalRequests((prev) => [
        { ...newRequest, createdAt: newRequest.createdAt || new Date() },
        ...prev,
      ]);
    } else if (newRequest.requestType === "Replacement") {
      setReplacementRequests((prev) => [
        { ...newRequest, createdAt: newRequest.createdAt || new Date() },
        ...prev,
      ]);
    }
  };

  const fetchMaintenanceLogs = async (assetId) => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(
        `${API_URL}/api/maintenance-logs/asset/${assetId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setMaintenanceLogs(res.data.data || []);
    } catch (err) {
      console.error("Failed to fetch maintenance logs:", err);
      toast.error("Failed to load maintenance logs ❌");
    }
  };

  const filteredAssets = assets.filter((a) => {
    const matchesSearch =
      (a?.name || "").toLowerCase().includes((search || "").toLowerCase()) ||
      (a?.code || "").toLowerCase().includes((search || "").toLowerCase());
    const matchesStatus = statusFilter === "All" || a?.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const sortedFilteredAssets = [...filteredAssets].sort(
    (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
  );

  const paginatedAssets = sortedFilteredAssets.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  const extractAssetIdFromRequest = (req) => {
    if (!req) return null;
    if (typeof req.asset === "string") return req.asset;
    if (req.asset && typeof req.asset === "object") {
      return req.asset._id || req.asset.id || null;
    }
    if (req.assetId) return req.assetId;
    if (req.asset_id) return req.asset_id;
    if (req.assetRef) return req.assetRef;
    return null;
  };

  const attachAssetInfoToRequests = (requestsArray) => {
    return (requestsArray || []).map((r) => {
      const assetId = extractAssetIdFromRequest(r);
      const assetObj =
        assets.find((a) => a._id === assetId) ||
        assets.find((a) => a.code === assetId) ||
        null;
      return {
        ...r,
        serialNumber: assetObj ? assetObj.serialNumber : r.serialNumber || "N/A",
        name: assetObj ? assetObj.name : r.name || "N/A",
        _assetObj: assetObj,
      };
    });
  };

  const handleView = (asset) => {
    setSelectedAsset(asset);
    fetchMaintenanceLogs(asset._id);
    setOpenDetail(true);
  };

  const handleSubmitDisposal = async () => {
    try {
      const token = localStorage.getItem("token");
      const { data } = await axios.post(
        `${API_URL}/api/assets/${selectedAsset._id}/disposal-requests`,
        {
          reason: disposalData.reason,
          justification: disposalData.justification,
          requestType:
            disposalData.requestType === "Replacement" ? "Replacement" : "Disposal",
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Request submitted ✅");
      setOpenDisposal(false);
      addRequestLocally(data.data);
      setDisposalData({ requestType: "", reason: "", justification: "" });
    } catch (error) {
      console.error("Failed to submit request:", error.response?.data || error.message);
      toast.error(error.response?.data?.message || "Failed to submit request ❌");
    }
  };

  const handleStatusUpdate = async (newStatus) => {
    if (!selectedAsset) return;
    try {
      const token = localStorage.getItem("token");
      const user = JSON.parse(localStorage.getItem("user"));
      const payload = {
        status: newStatus,
        updatedBy: user?._id,
      };
      await axios.put(`${API_URL}/api/assets/${selectedAsset._id}`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAssets((prevAssets) =>
        prevAssets.map((asset) =>
          asset._id === selectedAsset._id
            ? { ...asset, status: newStatus }
            : asset
        )
      );
      setSelectedAsset((prev) => ({ ...prev, status: newStatus }));
      setOpenStatus(false);
      toast.success("Status updated successfully ✅");
    } catch (error) {
      console.error("Status update failed:", error);
      toast.error("Status update failed ❌");
    }
  };

  const handleEditRequest = (request) => {
    setDisposalData({
      requestType: request.requestType === "Replacement" ? "Replacement" : "Disposal",
      reason: request.reason || "",
      justification: request.justification || "",
      requestId: request._id || request.id,
    });
    if (request._assetObj) {
      setSelectedAsset(request._assetObj);
    } else {
      const fallbackId = extractAssetIdFromRequest(request);
      if (fallbackId) {
        const assetObj = assets.find((a) => a._id === fallbackId);
        if (assetObj) setSelectedAsset(assetObj);
      }
    }
    setOpenDisposal(true);
  };

  // Mobile responsive columns
  const mobileColumns = [
    { 
      title: "Name", 
      dataIndex: "name", 
      key: "name",
      ellipsis: true,
      render: (text, record) => (
        <div>
          <div className="font-medium text-gray-800 text-sm">{text}</div>
          <div className="text-xs text-gray-500">Serial: {record.serialNumber}</div>
        </div>
      )
    },
    { 
      title: "Status", 
      dataIndex: "status", 
      key: "status",
      width: 100,
      render: (status) => {
        const colors = {
          Active: "green",
          "Needs Review": "orange",
          "Under Maintenance": "blue",
          Damaged: "red",
          Disposed: "gray",
        };
        return (
          <Badge 
            color={colors[status] || "default"} 
            text={<span className="text-xs">{status}</span>}
            className="whitespace-nowrap"
          />
        );
      }
    },
    {
      title: "Actions",
      key: "actions",
      width: 80,
      render: (_, record) => (
        <Button
          type="primary"
          icon={<EyeOutlined />}
          onClick={() => handleView(record)}
          className="bg-orange-400 hover:bg-orange-500 border-orange-400 hover:border-orange-500"
          size="small"
        >
          <span className="hidden xs:inline">View</span>
        </Button>
      ),
    },
  ];

  const desktopColumns = [
    { 
      title: "Serial Number", 
      dataIndex: "serialNumber", 
      key: "serialNumber"
    },
    { 
      title: "Name", 
      dataIndex: "name", 
      key: "name"
    },
    { 
      title: "Status", 
      dataIndex: "status", 
      key: "status",
      render: (status) => {
        const colors = {
          Active: "green",
          "Needs Review": "orange",
          "Under Maintenance": "blue",
          Damaged: "red",
          Disposed: "gray",
        };
        return (
          <Badge 
            color={colors[status] || "default"} 
            text={status}
            className="whitespace-nowrap"
          />
        );
      }
    },
    {
      title: "Last Service",
      key: "lastService",
      render: (_, record) =>
        record.lastServiced
          ? new Date(record.lastServiced).toLocaleDateString("en-GB")
          : "N/A",
    },
    {
      title: "Next Service",
      key: "nextService",
      render: (_, record) =>
        record.nextMaintenanceDate
          ? new Date(record.nextMaintenanceDate).toLocaleDateString("en-GB")
          : "N/A",
    },
    {
      title: "Actions",
      key: "actions",
      width: 100,
      render: (_, record) => (
        <Button
          type="primary"
          icon={<EyeOutlined />}
          onClick={() => handleView(record)}
          className="bg-orange-400 hover:bg-orange-500 border-orange-400 hover:border-orange-500"
        >
          View
        </Button>
      ),
    },
  ];

  const mobileRequestColumns = [
    { 
      title: "Asset", 
      key: "asset",
      ellipsis: true,
      render: (_, record) => (
        <div>
          <div className="font-medium text-gray-800 text-sm">{record.name}</div>
          <div className="text-xs text-gray-500">Serial: {record.serialNumber}</div>
          <div className="text-xs text-gray-500 truncate">{record.reason}</div>
        </div>
      )
    },
    { 
      title: "Status", 
      dataIndex: "status", 
      key: "status",
      width: 80,
      render: (status) => {
        const colors = {
          approved: "green",
          pending: "orange",
          rejected: "red",
        };
        return (
          <Badge 
            color={colors[status?.toLowerCase()] || "default"} 
            text={<span className="text-xs">{status || "Pending"}</span>}
          />
        );
      }
    },
    {
      title: "Actions",
      key: "requestActions",
      width: 70,
      render: (_, record) => {
        const disabled = (record.status || "").toLowerCase() === "approved";
        return (
          <Button
            disabled={disabled}
            onClick={() => handleEditRequest(record)}
            icon={<EditOutlined />}
            className={`${!disabled ? "bg-orange-400 hover:bg-orange-500 border-orange-400 hover:border-orange-500 text-white" : ""}`}
            type={disabled ? "default" : "primary"}
            size="small"
          >
            <span className="hidden xs:inline">Edit</span>
          </Button>
        );
      },
    },
  ];

  const desktopRequestColumns = [
    { 
      title: "Serial", 
      dataIndex: "serialNumber", 
      key: "serialNumber"
    },
    { 
      title: "Name", 
      dataIndex: "name", 
      key: "name" 
    },
    { 
      title: "Reason", 
      dataIndex: "reason", 
      key: "reason",
      ellipsis: true
    },
    { 
      title: "Status", 
      dataIndex: "status", 
      key: "status",
      render: (status) => {
        const colors = {
          approved: "green",
          pending: "orange",
          rejected: "red",
        };
        return (
          <Badge 
            color={colors[status?.toLowerCase()] || "default"} 
            text={status || "Pending"}
          />
        );
      }
    },
    {
      title: "Actions",
      key: "requestActions",
      width: 100,
      render: (_, record) => {
        const disabled = (record.status || "").toLowerCase() === "approved";
        return (
          <Button
            disabled={disabled}
            onClick={() => handleEditRequest(record)}
            icon={<EditOutlined />}
            className={`${!disabled ? "bg-orange-400 hover:bg-orange-500 border-orange-400 hover:border-orange-500 text-white" : ""}`}
            type={disabled ? "default" : "primary"}
          >
            Edit
          </Button>
        );
      },
    },
  ];

  const disposalRequestsWithAsset = attachAssetInfoToRequests(disposalRequests).filter(
    (r) => r.requestType === "Disposal"
  );
  const replacementRequestsWithAsset = attachAssetInfoToRequests(replacementRequests).filter(
    (r) => r.requestType === "Replacement"
  );

  // Check if mobile view
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);

  useEffect(() => {
    const checkDevice = () => {
      setIsMobile(window.innerWidth < 768);
      setIsTablet(window.innerWidth >= 768 && window.innerWidth < 1024);
    };

    checkDevice();
    window.addEventListener('resize', checkDevice);
    return () => window.removeEventListener('resize', checkDevice);
  }, []);

  const columns = isMobile ? mobileColumns : desktopColumns;
  const requestColumns = isMobile ? mobileRequestColumns : desktopRequestColumns;

  return (
    <div className="flex bg-gray-50 min-h-screen">
      <HOSidebar
        visible={sidebarVisible}
        onClose={() => setSidebarVisible(false)}
      />

      <div className={`flex-1 ${isMobile ? '' : 'lg:ml-64'}`}>
        <Topbar onSidebarToggle={() => setSidebarVisible(true)} />

        <main className="mt-16 p-2 sm:p-3 md:p-4 lg:p-6">
          {/* Header with Icon */}
          <div className="mb-3 sm:mb-4 md:mb-5">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="bg-orange-50 p-2 rounded-lg flex-shrink-0">
                <ToolOutlined className="text-orange-400 text-lg sm:text-xl" />
              </div>
              <div className="flex-1 min-w-0">
                <h1 className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold text-gray-800 m-0 truncate">
                  Equipment Maintenance
                </h1>
                <p className="text-gray-600 text-xs sm:text-sm mt-1">
                  Manage assets, track maintenance, and handle disposal requests
                </p>
              </div>
            </div>
          </div>

          {/* Compact Filters */}
          <Card className="mb-3 sm:mb-4 md:mb-5 shadow-sm" bodyStyle={{ padding: isMobile ? '8px' : '12px' }}>
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
              <Input
                placeholder="Search by asset code or name..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                prefix={<SearchOutlined className="text-gray-400" />}
                className="flex-1 text-sm"
                size={isMobile ? "small" : "middle"}
              />
              <Select
                value={statusFilter}
                onChange={setStatusFilter}
                className="w-full sm:w-40"
                suffixIcon={<FilterOutlined className="text-orange-400" />}
                size={isMobile ? "small" : "middle"}
              >
                <Option value="All">All Statuses</Option>
                <Option value="Active">Active</Option>
                <Option value="Needs Review">Needs Review</Option>
                <Option value="Under Maintenance">Under Maintenance</Option>
                <Option value="Damaged">Damaged</Option>
                <Option value="Disposed">Disposed</Option>
                <Option value="Pending Return">Pending Return</Option>
                <Option value="Returned">Returned</Option>
              </Select>
            </div>
          </Card>

          {/* Tabs */}
          <Card className="shadow-sm">
            <Tabs 
              activeKey={activeTab} 
              onChange={setActiveTab}
              className="mobile-tabs"
              size={isMobile ? "small" : "middle"}
              tabBarStyle={{ marginBottom: 0, padding: '0 8px' }}
            >
              <TabPane 
                tab={
                  <span className="flex items-center gap-1 sm:gap-2">
                    <span className="text-xs sm:text-sm">Equipment</span>
                    <Badge 
                      count={sortedFilteredAssets.length} 
                      style={{ backgroundColor: "#fb923c" }}
                      size="small"
                    />
                  </span>
                } 
                key="1"
              >
                {loading && (
                  <div className="py-8 sm:py-12 text-center text-gray-500">
                    <div className="animate-spin rounded-full h-8 w-8 sm:h-12 sm:w-12 border-b-2 border-orange-400 mx-auto mb-3 sm:mb-4"></div>
                    <div className="text-sm">Loading assets...</div>
                  </div>
                )}
                {!loading && error && (
                  <div className="py-8 sm:py-12 text-center">
                    <Empty description={error} />
                  </div>
                )}
                {!loading && !error && (
                  <>
                    <div className="overflow-x-auto">
                      <Table
                        dataSource={paginatedAssets}
                        columns={columns}
                        rowKey="code"
                        pagination={false}
                        locale={{ emptyText: <Empty description="No equipment found" /> }}
                        scroll={{ x: isMobile ? 350 : 800 }}
                        size={isMobile ? "small" : "middle"}
                        className="responsive-table"
                      />
                    </div>
                    {sortedFilteredAssets.length > 0 && (
                      <div className="mt-3 sm:mt-4 flex flex-col sm:flex-row justify-between items-center gap-2 sm:gap-3 px-1">
                        <div className="text-xs sm:text-sm text-gray-600 text-center sm:text-left">
                          Showing {((currentPage - 1) * pageSize) + 1} to{" "}
                          {Math.min(currentPage * pageSize, sortedFilteredAssets.length)} of{" "}
                          {sortedFilteredAssets.length} entries
                        </div>
                        <div className="flex gap-1 sm:gap-2 items-center">
                          <Button
                            disabled={currentPage === 1}
                            onClick={() => setCurrentPage((p) => p - 1)}
                            size={isMobile ? "small" : "middle"}
                            className="text-xs"
                          >
                            Previous
                          </Button>
                          <span className="px-2 text-xs sm:text-sm">
                            {currentPage} / {Math.ceil(sortedFilteredAssets.length / pageSize) || 1}
                          </span>
                          <Button
                            disabled={
                              currentPage === Math.ceil(sortedFilteredAssets.length / pageSize)
                            }
                            onClick={() => setCurrentPage((p) => p + 1)}
                            size={isMobile ? "small" : "middle"}
                            className="text-xs"
                          >
                            Next
                          </Button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </TabPane>

              <TabPane 
                tab={
                  <span className="flex items-center gap-1 sm:gap-2">
                    <span className="text-xs sm:text-sm">Replacements</span>
                    <Badge 
                      count={replacementRequestsWithAsset.length} 
                      style={{ backgroundColor: "#fb923c" }}
                      size="small"
                    />
                  </span>
                } 
                key="2"
              >
                <div className="overflow-x-auto">
                  <Table
                    dataSource={replacementRequestsWithAsset.slice(
                      (replacementPage - 1) * requestPageSize,
                      replacementPage * requestPageSize
                    )}
                    columns={requestColumns}
                    rowKey="_id"
                    pagination={false}
                    locale={{ emptyText: <Empty description="No replacement requests yet" /> }}
                    scroll={{ x: isMobile ? 350 : 700 }}
                    size={isMobile ? "small" : "middle"}
                    className="responsive-table"
                  />
                </div>
                {replacementRequestsWithAsset.length > requestPageSize && (
                  <div className="mt-3 sm:mt-4 flex flex-col sm:flex-row justify-between items-center gap-2 sm:gap-3 px-1">
                    <div className="text-xs sm:text-sm text-gray-600 text-center sm:text-left">
                      Page {replacementPage} of{" "}
                      {Math.ceil(replacementRequestsWithAsset.length / requestPageSize)}
                    </div>
                    <div className="flex gap-1 sm:gap-2">
                      <Button
                        disabled={replacementPage === 1}
                        onClick={() => setReplacementPage((p) => p - 1)}
                        size={isMobile ? "small" : "middle"}
                      >
                        Previous
                      </Button>
                      <Button
                        disabled={
                          replacementPage ===
                          Math.ceil(replacementRequestsWithAsset.length / requestPageSize)
                        }
                        onClick={() => setReplacementPage((p) => p + 1)}
                        size={isMobile ? "small" : "middle"}
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                )}
              </TabPane>

              <TabPane 
                tab={
                  <span className="flex items-center gap-1 sm:gap-2">
                    <span className="text-xs sm:text-sm">Disposals</span>
                    <Badge 
                      count={disposalRequestsWithAsset.length} 
                      style={{ backgroundColor: "#fb923c" }}
                      size="small"
                    />
                  </span>
                } 
                key="3"
              >
                <div className="overflow-x-auto">
                  <Table
                    dataSource={disposalRequestsWithAsset.slice(
                      (disposalPage - 1) * requestPageSize,
                      disposalPage * requestPageSize
                    )}
                    columns={requestColumns}
                    rowKey="_id"
                    pagination={false}
                    locale={{ emptyText: <Empty description="No disposal requests yet" /> }}
                    scroll={{ x: isMobile ? 350 : 700 }}
                    size={isMobile ? "small" : "middle"}
                    className="responsive-table"
                  />
                </div>
                {disposalRequestsWithAsset.length > requestPageSize && (
                  <div className="mt-3 sm:mt-4 flex flex-col sm:flex-row justify-between items-center gap-2 sm:gap-3 px-1">
                    <div className="text-xs sm:text-sm text-gray-600 text-center sm:text-left">
                      Page {disposalPage} of{" "}
                      {Math.ceil(disposalRequestsWithAsset.length / requestPageSize)}
                    </div>
                    <div className="flex gap-1 sm:gap-2">
                      <Button
                        disabled={disposalPage === 1}
                        onClick={() => setDisposalPage((p) => p - 1)}
                        size={isMobile ? "small" : "middle"}
                      >
                        Previous
                      </Button>
                      <Button
                        disabled={
                          disposalPage ===
                          Math.ceil(disposalRequestsWithAsset.length / requestPageSize)
                        }
                        onClick={() => setDisposalPage((p) => p + 1)}
                        size={isMobile ? "small" : "middle"}
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                )}
              </TabPane>
            </Tabs>
          </Card>
        </main>
      </div>

      {/* Enhanced Modals */}
      <EnhancedAssetDetailModal
        open={openDetail}
        onClose={() => setOpenDetail(false)}
        asset={selectedAsset}
        maintenanceLogs={maintenanceLogs}
        onUpdateStatus={() => {
          setNewStatus(selectedAsset?.status);
          setOpenStatus(true);
        }}
        onDisposal={() => {
          setOpenDetail(false);
          setOpenDisposal(true);
        }}
      />

      <EnhancedStatusModal
        open={openStatus}
        onClose={() => setOpenStatus(false)}
        currentStatus={newStatus}
        statusOptions={[
          { value: "Active", label: "Active" },
          { value: "Needs Review", label: "Needs Review" },
          { value: "Under Maintenance", label: "Under Maintenance" },
          { value: "Damaged", label: "Damaged" },
          { value: "Disposed", label: "Disposed" },
          { value: "Pending Return", label: "Pending Return" },
          { value: "Returned", label: "Returned" },
        ]}
        onStatusChange={setNewStatus}
        onSave={() => handleStatusUpdate(newStatus)}
      />

      <EnhancedDisposalModal
        open={openDisposal}
        onClose={() => setOpenDisposal(false)}
        disposalData={disposalData}
        setDisposalData={setDisposalData}
        onSubmit={handleSubmitDisposal}
      />

      <style jsx global>{`
        .mobile-tabs .ant-tabs-tab {
          padding: 8px 4px;
          font-weight: 500;
          color: #6b7280;
          margin: 0 4px;
          font-size: 12px;
        }
        
        .mobile-tabs .ant-tabs-tab:hover {
          color: #fb923c;
        }
        
        .mobile-tabs .ant-tabs-tab-active .ant-tabs-tab-btn {
          color: #fb923c !important;
        }
        
        .mobile-tabs .ant-tabs-ink-bar {
          background: #fb923c;
          height: 2px;
        }

        .ant-table-thead > tr > th {
          background: #fef7ed !important;
          font-weight: 600;
          color: #1f2937;
          font-size: 12px;
          padding: 8px 4px !important;
        }

        .ant-table-tbody > tr > td {
          font-size: 12px;
          padding: 8px 4px !important;
        }

        .ant-badge-count {
          font-size: 10px;
          height: 16px;
          line-height: 16px;
          min-width: 16px;
        }

        .mobile-modal .ant-modal-content {
          border-radius: 8px;
          overflow: hidden;
        }

        .mobile-modal .ant-modal-header {
          border-bottom: none;
          padding: 12px 12px 0;
        }

        .mobile-modal .ant-modal-body {
          padding: 12px;
        }

        .mobile-modal .ant-radio-wrapper {
          width: 100%;
          margin: 0;
        }

        .mobile-modal .ant-radio-wrapper:hover {
          background-color: #fffbeb;
        }

        /* Responsive table styles */
        @media (max-width: 767px) {
          .responsive-table .ant-table {
            font-size: 11px;
          }
          
          .responsive-table .ant-table-thead > tr > th {
            padding: 6px 2px !important;
            font-size: 11px;
          }
          
          .responsive-table .ant-table-tbody > tr > td {
            padding: 6px 2px !important;
            font-size: 11px;
          }
          
          .responsive-table .ant-btn {
            padding: 0 4px;
            font-size: 11px;
            height: 24px;
          }
        }

        /* Override default blue hover for all buttons in this component */
        .ant-btn-primary:not([disabled]):not(.ant-btn-background-ghost):hover {
          background: #fb923c !important;
          border-color: #fb923c !important;
        }

        /* Extra small screen adjustments */
        @media (max-width: 480px) {
          .ant-btn > .anticon + span {
            margin-left: 2px;
          }
          
          .ant-badge-count {
            transform: scale(0.8);
            transform-origin: 100% 0%;
          }
          
          .mobile-tabs .ant-tabs-tab {
            padding: 6px 2px;
            margin: 0 2px;
            font-size: 11px;
          }
        }

        /* Ensure proper spacing on very small screens */
        @media (max-width: 360px) {
          .mobile-tabs .ant-tabs-tab {
            padding: 4px 1px;
            margin: 0 1px;
            font-size: 10px;
          }
          
          .ant-btn {
            padding: 0 6px;
            font-size: 10px;
            height: 22px;
          }
          
          .ant-table-thead > tr > th {
            font-size: 10px;
          }
          
          .ant-table-tbody > tr > td {
            font-size: 10px;
          }
        }
        
        /* Tablet specific styles */
        @media (min-width: 768px) and (max-width: 1023px) {
          .ant-table-thead > tr > th {
            padding: 8px 6px !important;
            font-size: 12px;
          }
          
          .ant-table-tbody > tr > td {
            padding: 8px 6px !important;
            font-size: 12px;
          }
        }
      `}</style>
    </div>
  );
}